import { ActionsDropdown } from '@app/components/actions-dropdown/ActionsDropdown';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { TextField } from '@app/components/form-fields/TextField';
import useChannelPartners from '@app/hooks/channelPartners/useChannelPartners';
import type { ContactListParams } from '@app/services/contact.types';
import type { MenuProps } from 'antd';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';

export type BrandFiltersProps = {
    onChange: (values: ContactListParams) => void;
};

export const BrandFilters = ({ onChange }: BrandFiltersProps) => {
    const { data: channelPartnersData } = useChannelPartners({
        per_page: 99999,
    });

    const [filterSelection, setFilterSelection] = useState<ContactListParams>({
        search: '',
        type_id: [],
        brand_id: [],
        location_id: [],
    });

    useEffect(() => {
        onChange(filterSelection);
    }, [filterSelection]);

    const updateFilterSelection = (type: keyof typeof filterSelection, value: string[]) => {
        if (value.includes('reset')) {
            setFilterSelection((prev) => ({
                ...prev,
                [type]: [],
            }));
            return;
        }
        setFilterSelection((prev) => ({
            ...prev,
            [type]: value,
        }));
    };

    const generateFilterOptions = (options: MenuProps['items'] = []): MenuProps['items'] => {
        if (!options) {
            return [];
        }

        return [
            ...options,
            { type: 'divider' },
            {
                key: 'reset',
                label: <span className="text-red">Clear</span>,
            },
        ];
    };

    const channelPartnerOptions =
        channelPartnersData?.data?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const filters = [
        {
            id: 'channel_id',
            label: 'Channel Partner',
            multiple: true,
            items: generateFilterOptions(channelPartnerOptions),
        },
    ];

    return (
        <div>
            <TextField
                placeholder="Search brands..."
                prefix={<MdSearch size={20} />}
                onChange={debounce((e) => {
                    setFilterSelection((prev) => ({
                        ...prev,
                        search: e.target.value,
                    }));
                }, 500)}
            />
            <div className="styled-table-filters">
                <StyledButtonGroup justify="flex-start">
                    {filters.map((filter) => {
                        const itemId = filter.id as keyof ContactListParams;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const value = filterSelection?.[itemId] as any;
                        return (
                            <ActionsDropdown
                                key={filter.id}
                                title={
                                    <>
                                        {filter.label}
                                        {value?.length ? <strong> ({value?.length})</strong> : null}
                                    </>
                                }
                                items={filter.items || []}
                                menu={{
                                    selectable: true,
                                    selectedKeys: value,
                                    multiple: filter.multiple,
                                    onSelect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                    onDeselect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                }}
                                buttonProps={{
                                    size: 'small',
                                    shape: 'round',
                                    type: 'default',
                                    color: value?.length ? 'green' : 'default',
                                }}
                            />
                        );
                    })}
                </StyledButtonGroup>
            </div>
        </div>
    );
};

export default BrandFilters;
